import * as Sentry from '@sentry/react';
import { chain, flatten } from 'lodash';
import { FC } from 'react';
import { RouteObject, useRoutes } from 'react-router-dom';

import { Action } from './action';
import { Routes } from './routes';
import { Scope } from './scope';

/** Generate the routes for all the Action types */
const generateActionRoute = (action: Action): RouteObject => {
  return {
    path: action.fullPath,
    element: action.element,
  };
};

/** Generate the routes for all the Scope types */
const generateScopeRoute = (scope: Scope): RouteObject[] => {
  const scopeChildren = chain(scope.scopes).map(generateScopeRoute).flatten().value();
  const actionChildren = chain(scope.actions).map(generateActionRoute).value();

  return [
    {
      // If there is no element, we don't want to generate a path
      path: scope.element ? (scope.path ?? scope.name) : undefined,
      element: scope.element,
      children: flatten([scopeChildren, actionChildren]),
    },
  ];
};

/**
 * Generate routes for `react-router-dom`
 *
 * To be used like: <GeneratedRoutes router={router} />
 */
export const GeneratedRoutes: FC<{ routes: Routes }> = ({ routes }) => {
  const routeList = chain(routes.scopes).map(generateScopeRoute).flatten().value();

  /**
   * We use this patched `useRoutes` to be used with Sentry
   *
   * ref: https://docs.sentry.io/platforms/javascript/guides/react/features/react-router/#usage-with-useroutes-hook
   */
  const patchedUsedRoutes = Sentry.wrapUseRoutesV7(useRoutes);

  return patchedUsedRoutes(routeList);
};
